import { useEffect, useRef, useState } from "react";
import { Plus, PencilSimple, Trash, Wallet, Buildings, FloppyDisk, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { formatINR } from "../../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const DEPTS = ["Operations", "Sales", "Finance", "Admin", "HR", "Other"];
const today = () => new Date().toISOString().slice(0,10);
const thisMonth = () => new Date().toISOString().slice(0,7);

const empEmpty = { name: "", designation: "", department: "Operations", location: "", base_salary: 0, joining_date: today(), email: "", phone: "", active: true };
const salEmpty = { employee_id: "", month: thisMonth(), basic: 0, hra: 0, allowances: 0, incentive: 0, deductions: 0, notes: "", paid: false, paid_on: "" };

export default function Salaries() {
  const { active, activeId } = useCompany();
  const navigate = useNavigate();
  const [tab, setTab] = useState("salaries"); // employees | salaries
  const [employees, setEmployees] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [month, setMonth] = useState(thisMonth());

  const [empOpen, setEmpOpen] = useState(false);
  const [salOpen, setSalOpen] = useState(false);
  const [empForm, setEmpForm] = useState(empEmpty);
  const [salForm, setSalForm] = useState(salEmpty);
  const [editingEmp, setEditingEmp] = useState(null);
  const [editingSal, setEditingSal] = useState(null);
  const musterRef = useRef(null);
  const ctcRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const doUpload = async (file, kind) => {
    if (!activeId) { toast.error("Pick a company first"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const endpoint = kind === "ctc" ? "upload-ctc" : "upload-muster";
      const { data } = await api.post(
        `/finance/salaries/${endpoint}?company_id=${activeId}&month=${month}&auto_create=true`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const parts = [
        `${data.salaries_written} salary records`,
        data.matched_existing ? `${data.matched_existing} matched` : null,
        data.created_new_employees ? `${data.created_new_employees} new employees` : null,
        data.skipped ? `${data.skipped} skipped` : null,
      ].filter(Boolean).join(" · ");
      toast.success(`${kind === "ctc" ? "CTC Sheet" : "Muster"} imported — ${parts}`);
      if (data.unmatched_names?.length) toast.message(`${data.unmatched_names.length} unmatched name(s) — see backend logs`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const uploadMuster = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    await doUpload(f, "muster"); e.target.value = "";
  };
  const uploadCTC = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    await doUpload(f, "ctc"); e.target.value = "";
  };

  const load = async () => {
    if (!activeId) { setEmployees([]); setSalaries([]); return; }
    const [{ data: emps }, { data: sals }] = await Promise.all([
      api.get("/finance/employees", { params: { company_id: activeId } }),
      api.get("/finance/salaries", { params: { company_id: activeId, month } }),
    ]);
    setEmployees(emps);
    setSalaries(sals);
  };
  useEffect(() => { load(); }, [activeId, month]);

  const openEmpNew = () => { setEditingEmp(null); setEmpForm(empEmpty); setEmpOpen(true); };
  const openEmpEdit = (e) => { setEditingEmp(e); setEmpForm({ ...empEmpty, ...e }); setEmpOpen(true); };

  const saveEmp = async () => {
    if (!empForm.name) { toast.error("Name required"); return; }
    const payload = { ...empForm, company_id: activeId, base_salary: parseFloat(empForm.base_salary || 0) };
    if (editingEmp) await api.put(`/finance/employees/${editingEmp.id}`, payload);
    else await api.post("/finance/employees", payload);
    toast.success(editingEmp ? "Employee updated" : "Employee added");
    setEmpOpen(false); load();
  };

  const removeEmp = async (id) => {
    if (!window.confirm("Delete this employee?")) return;
    await api.delete(`/finance/employees/${id}`);
    toast.success("Deleted"); load();
  };

  const openSalNew = (preselectEmp = "") => {
    const emp = employees.find((e) => e.id === preselectEmp);
    setEditingSal(null);
    setSalForm({ ...salEmpty, month, employee_id: preselectEmp, basic: emp?.base_salary || 0 });
    setSalOpen(true);
  };
  const openSalEdit = (s) => { setEditingSal(s); setSalForm({ ...salEmpty, ...s, paid_on: s.paid_on || "" }); setSalOpen(true); };

  const saveSal = async () => {
    if (!salForm.employee_id) { toast.error("Pick an employee"); return; }
    const payload = { ...salForm, company_id: activeId,
      basic: parseFloat(salForm.basic || 0),
      hra: parseFloat(salForm.hra || 0),
      allowances: parseFloat(salForm.allowances || 0),
      incentive: parseFloat(salForm.incentive || 0),
      deductions: parseFloat(salForm.deductions || 0),
      paid_on: salForm.paid_on || null,
    };
    if (editingSal) await api.put(`/finance/salaries/${editingSal.id}`, payload);
    else await api.post("/finance/salaries", payload);
    toast.success("Saved");
    setSalOpen(false); load();
  };

  const removeSal = async (id) => {
    if (!window.confirm("Delete salary record?")) return;
    await api.delete(`/finance/salaries/${id}`);
    toast.success("Deleted"); load();
  };

  if (!activeId) {
    return (
      <Layout title="Salaries"><div className="border border-black/10 p-16 text-center"><Buildings size={36} className="mx-auto text-[#D90429]"/><h3 className="font-display text-2xl font-bold tracking-tight mt-4">Pick a company first</h3><button onClick={()=>navigate("/finance/companies")} className="mt-5 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">Go to Companies</button></div></Layout>
    );
  }

  const totalNet = salaries.reduce((s, r) => s + (Number(r.net_pay) || 0), 0);
  const totalIncent = salaries.reduce((s, r) => s + (Number(r.incentive) || 0), 0);
  const empById = Object.fromEntries(employees.map((e) => [e.id, e]));

  return (
    <Layout
      title="Payroll"
      subtitle={`${active?.name} · ${employees.length} employees · ${salaries.length} ${month} records`}
      actions={
        <>
          <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)} className="w-44 rounded-none" data-testid="salary-month-picker"/>
          <input ref={musterRef} type="file" accept=".csv,.xlsx,.xls" onChange={uploadMuster} className="hidden" data-testid="muster-upload-input"/>
          <input ref={ctcRef} type="file" accept=".csv,.xlsx,.xls" onChange={uploadCTC} className="hidden" data-testid="ctc-upload-input"/>
          <button onClick={()=>musterRef.current?.click()} disabled={uploading} data-testid="upload-muster-button" className="inline-flex items-center gap-2 px-4 py-2.5 border border-black/15 text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white transition disabled:opacity-50">
            <UploadSimple size={16} weight="bold"/> {uploading ? "Importing…" : "Import Muster"}
          </button>
          <button onClick={()=>ctcRef.current?.click()} disabled={uploading} data-testid="upload-ctc-button" className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#D90429] text-[#D90429] text-sm font-semibold hover:bg-[#D90429] hover:text-white transition disabled:opacity-50">
            <UploadSimple size={16} weight="bold"/> Import CTC Sheet
          </button>
          {tab === "employees" ? (
            <button onClick={openEmpNew} data-testid="add-employee-button" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] active:scale-95"><Plus size={16}/> Employee</button>
          ) : (
            <button onClick={()=>openSalNew()} data-testid="add-salary-button" disabled={!employees.length} className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] active:scale-95 disabled:opacity-50"><Plus size={16}/> Salary Entry</button>
          )}
        </>
      }
    >
      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-black/10">
        {["salaries", "employees"].map((t)=>(
          <button key={t} onClick={()=>setTab(t)} data-testid={`tab-${t}`} className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition ${tab===t ? "border-[#D90429] text-[#D90429]" : "border-transparent text-[#717680] hover:text-[#0A0A0A]"}`}>
            {t === "salaries" ? "Monthly Salaries" : "Employees"}
          </button>
        ))}
      </div>

      {tab === "salaries" && (
        <>
          <div className="grid grid-cols-3 border border-black/10 bg-white mb-6">
            <div className="p-5 border-r border-black/5"><div className="overline">Net pay this month</div><div className="font-display text-3xl font-black tracking-tighter mt-2">{formatINR(totalNet)}</div></div>
            <div className="p-5 border-r border-black/5"><div className="overline" style={{color:"#2A9D8F"}}>Incentives paid</div><div className="font-display text-3xl font-black tracking-tighter mt-2 text-[#2A9D8F]">{formatINR(totalIncent)}</div></div>
            <div className="p-5"><div className="overline">Records</div><div className="font-display text-3xl font-black tracking-tighter mt-2">{salaries.length}</div></div>
          </div>

          {salaries.length === 0 ? (
            <div className="border border-black/10 p-16 text-center text-[#717680]"><Wallet size={36} className="mx-auto text-[#D90429]"/><p className="mt-4">No salary records for {month}. Add a salary entry to start.</p></div>
          ) : (
            <div className="border border-black/10 bg-white overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]" data-testid="salaries-table">
                <thead className="bg-[#F5F6F8] text-left"><tr>{["Employee","Dept","Basic","HRA","Allow.","Incentive","Deduct.","Net Pay","Status",""].map(h=>(<th key={h} className="px-3 py-3 font-mono-shelv text-xs uppercase tracking-widest text-[#717680]">{h}</th>))}</tr></thead>
                <tbody>
                  {salaries.map((s)=>{
                    const e = empById[s.employee_id];
                    return (
                      <tr key={s.id} className="border-t border-black/5" data-testid={`salary-row-${s.id}`}>
                        <td className="px-3 py-2"><div className="font-semibold">{e?.name || "—"}</div><div className="text-xs text-[#717680]">{e?.designation}</div></td>
                        <td className="px-3 py-2 text-[#4A4D57]">{e?.department}</td>
                        <td className="px-3 py-2 font-mono-shelv">{formatINR(s.basic)}</td>
                        <td className="px-3 py-2 font-mono-shelv">{formatINR(s.hra)}</td>
                        <td className="px-3 py-2 font-mono-shelv">{formatINR(s.allowances)}</td>
                        <td className="px-3 py-2 font-mono-shelv text-[#2A9D8F]">{formatINR(s.incentive)}</td>
                        <td className="px-3 py-2 font-mono-shelv text-[#D90429]">{formatINR(s.deductions)}</td>
                        <td className="px-3 py-2 font-mono-shelv font-bold">{formatINR(s.net_pay)}</td>
                        <td className="px-3 py-2"><span className={`text-xs font-mono-shelv uppercase tracking-widest px-2 py-0.5 ${s.paid ? "bg-[#2A9D8F]/15 text-[#2A9D8F]" : "bg-[#F4A261]/15 text-[#F4A261]"}`}>{s.paid ? "Paid" : "Pending"}</span></td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex gap-1">
                            <button onClick={()=>openSalEdit(s)} className="p-1.5 hover:bg-black/5" data-testid={`edit-salary-${s.id}`}><PencilSimple size={13}/></button>
                            <button onClick={()=>removeSal(s.id)} className="p-1.5 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`delete-salary-${s.id}`}><Trash size={13}/></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "employees" && (
        <>
          {employees.length === 0 ? (
            <div className="border border-black/10 p-16 text-center text-[#717680]"><Wallet size={36} className="mx-auto text-[#D90429]"/><p className="mt-4">No employees yet. Add your first team member.</p></div>
          ) : (
            <div className="border border-black/10 bg-white overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]" data-testid="employees-table">
                <thead className="bg-[#F5F6F8] text-left"><tr>{["Name","Designation","Department","Location","Base Salary","Joined","Status",""].map(h=>(<th key={h} className="px-3 py-3 font-mono-shelv text-xs uppercase tracking-widest text-[#717680]">{h}</th>))}</tr></thead>
                <tbody>
                  {employees.map((e)=>(
                    <tr key={e.id} className="border-t border-black/5" data-testid={`employee-row-${e.id}`}>
                      <td className="px-3 py-2"><div className="font-semibold">{e.name}</div><div className="text-xs text-[#717680] font-mono-shelv">{e.email}</div></td>
                      <td className="px-3 py-2 text-[#4A4D57]">{e.designation || "—"}</td>
                      <td className="px-3 py-2"><span className="text-xs font-mono-shelv uppercase tracking-widest bg-black/5 px-2 py-0.5">{e.department}</span></td>
                      <td className="px-3 py-2 text-[#4A4D57]">{e.location || "—"}</td>
                      <td className="px-3 py-2 font-mono-shelv">{formatINR(e.base_salary)}</td>
                      <td className="px-3 py-2 text-[#4A4D57]">{e.joining_date || "—"}</td>
                      <td className="px-3 py-2"><span className={`text-xs font-mono-shelv uppercase tracking-widest px-2 py-0.5 ${e.active ? "bg-[#2A9D8F]/15 text-[#2A9D8F]" : "bg-black/10 text-[#717680]"}`}>{e.active ? "Active" : "Inactive"}</span></td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1">
                          <button onClick={()=>openSalNew(e.id)} className="text-[10px] font-mono-shelv uppercase tracking-widest text-[#D90429] hover:underline px-2" data-testid={`pay-salary-${e.id}`}>Pay {month}</button>
                          <button onClick={()=>openEmpEdit(e)} className="p-1.5 hover:bg-black/5" data-testid={`edit-employee-${e.id}`}><PencilSimple size={13}/></button>
                          <button onClick={()=>removeEmp(e.id)} className="p-1.5 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`delete-employee-${e.id}`}><Trash size={13}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Employee dialog */}
      <Dialog open={empOpen} onOpenChange={setEmpOpen}>
        <DialogContent className="rounded-none max-w-2xl">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editingEmp ? "Edit employee" : "New employee"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Name *</Label><Input data-testid="emp-name-input" value={empForm.name} onChange={(e)=>setEmpForm({...empForm, name:e.target.value})} className="rounded-none"/></div>
            <div><Label>Designation</Label><Input value={empForm.designation} onChange={(e)=>setEmpForm({...empForm, designation:e.target.value})} className="rounded-none"/></div>
            <div><Label>Department</Label><Select value={empForm.department} onValueChange={(v)=>setEmpForm({...empForm, department:v})}><SelectTrigger className="rounded-none"><SelectValue/></SelectTrigger><SelectContent>{DEPTS.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Location</Label><Input value={empForm.location} onChange={(e)=>setEmpForm({...empForm, location:e.target.value})} className="rounded-none"/></div>
            <div><Label>Base Salary (₹)</Label><Input type="number" data-testid="emp-base-salary-input" value={empForm.base_salary} onChange={(e)=>setEmpForm({...empForm, base_salary:e.target.value})} className="rounded-none"/></div>
            <div><Label>Joining Date</Label><Input type="date" value={empForm.joining_date} onChange={(e)=>setEmpForm({...empForm, joining_date:e.target.value})} className="rounded-none"/></div>
            <div><Label>Email</Label><Input value={empForm.email} onChange={(e)=>setEmpForm({...empForm, email:e.target.value})} className="rounded-none"/></div>
            <div><Label>Phone</Label><Input value={empForm.phone} onChange={(e)=>setEmpForm({...empForm, phone:e.target.value})} className="rounded-none"/></div>
          </div>
          <DialogFooter>
            <button onClick={()=>setEmpOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={saveEmp} data-testid="save-employee-button" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Salary dialog */}
      <Dialog open={salOpen} onOpenChange={setSalOpen}>
        <DialogContent className="rounded-none max-w-2xl">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editingSal ? "Edit salary" : "New salary entry"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Employee *</Label>
              <Select value={salForm.employee_id} onValueChange={(v)=>{ const emp = empById[v]; setSalForm((f)=>({...f, employee_id:v, basic: f.basic || emp?.base_salary || 0})); }}>
                <SelectTrigger className="rounded-none" data-testid="sal-employee-select"><SelectValue placeholder="Pick employee…"/></SelectTrigger>
                <SelectContent>{employees.map((e)=>(<SelectItem key={e.id} value={e.id}>{e.name} — {e.designation || e.department}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div><Label>Month</Label><Input type="month" value={salForm.month} onChange={(e)=>setSalForm({...salForm, month:e.target.value})} className="rounded-none"/></div>
            <div><Label>Basic</Label><Input type="number" data-testid="sal-basic-input" value={salForm.basic} onChange={(e)=>setSalForm({...salForm, basic:e.target.value})} className="rounded-none"/></div>
            <div><Label>HRA</Label><Input type="number" value={salForm.hra} onChange={(e)=>setSalForm({...salForm, hra:e.target.value})} className="rounded-none"/></div>
            <div><Label>Allowances</Label><Input type="number" value={salForm.allowances} onChange={(e)=>setSalForm({...salForm, allowances:e.target.value})} className="rounded-none"/></div>
            <div><Label>Incentive</Label><Input type="number" data-testid="sal-incentive-input" value={salForm.incentive} onChange={(e)=>setSalForm({...salForm, incentive:e.target.value})} className="rounded-none"/></div>
            <div><Label>Deductions</Label><Input type="number" value={salForm.deductions} onChange={(e)=>setSalForm({...salForm, deductions:e.target.value})} className="rounded-none"/></div>
            <div className="col-span-2 flex items-center gap-3 border border-black/10 px-3 py-2">
              <input type="checkbox" id="sal-paid" checked={!!salForm.paid} onChange={(e)=>setSalForm({...salForm, paid:e.target.checked, paid_on: e.target.checked && !salForm.paid_on ? today() : salForm.paid_on})}/>
              <label htmlFor="sal-paid" className="text-sm">Mark as paid</label>
              {salForm.paid && (
                <Input type="date" value={salForm.paid_on} onChange={(e)=>setSalForm({...salForm, paid_on:e.target.value})} className="rounded-none h-8 ml-auto w-44"/>
              )}
            </div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={salForm.notes} onChange={(e)=>setSalForm({...salForm, notes:e.target.value})} className="rounded-none"/></div>
            <div className="col-span-2 border-t border-black/10 pt-3 flex items-center justify-between">
              <span className="text-sm text-[#717680]">Net pay (Basic + HRA + Allow. + Incentive − Deductions)</span>
              <span className="font-display text-2xl font-black tracking-tighter" data-testid="sal-net-pay">{formatINR((Number(salForm.basic)||0)+(Number(salForm.hra)||0)+(Number(salForm.allowances)||0)+(Number(salForm.incentive)||0)-(Number(salForm.deductions)||0))}</span>
            </div>
          </div>
          <DialogFooter>
            <button onClick={()=>setSalOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={saveSal} data-testid="save-salary-button" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]"><FloppyDisk size={14} className="inline mr-1"/> Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
